import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';

import {
	Avatar,
	Box,
	Chip,
	Container,
	Divider,
	InputAdornment,
	MenuItem,
	Paper,
	Tooltip,
	Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';

import {
	ChildDto,
	CustomFieldDto,
	EngagementDto,
	EngagementStatusDto,
	FamilyDto,
	LanguageEnum,
	PermissionDto,
	ProviderDto,
	SurveyStatusColorEnum,
	UserRolesEnum
} from '@families-link/shared';

import {
	useArchiveEngagementMutation,
	useBulkArchiveEngagementsMutation,
	useBulkDeleteEngagementsMutation,
	useCreateEngagementMutation,
	useDeleteEngagementMutation,
	useExportEngagementsToCSVMutation,
	useGenerateEngagementsCustomFieldFileUrlMutation,
	useGetActiveEngagementCountLinguistLinkQuery,
	useGetActiveEngagementCountQuery,
	useGetActiveEngagementStatisticLinguistLinkQuery,
	useGetEngagementPieChartDataQuery,
	useGetEngagementsFromLLByDistrictNameQuery,
	useGetEngagementsListQuery,
	useGetEngagementsListUnifiedQuery,
	useGetSchoolsLinguistLinkQuery,
	useGetSelectedEngagementsFromLLQuery,
	useSendRemindersLinguistLinkEngagementsMutation,
	useUnarchiveEngagementMutation,
	useUpdateBulkEngagementsMutation,
	useUpdateEngagementMutation,
} from '../../../../store/api/engagements.api';

import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { CoreButton, CoreIconButton } from '../../../_core/components/_ui/core-button';
import { EngagementsTable } from '../../components/engagements-table';
import { DistrictRoutes } from '../../../district/constants';
import {
	useSearch,
	useSortingParams,
	usePaginatedData,
	useLanguageFilter,
	useProviderFilter,
	useDurationFilter,
	useMuiModal,
	DurationFilter,
} from '../../../_core/hooks';
import { AutocompleteTextField, CoreAutoComplete } from '../../../_core/components/_ui/core-autocomplete';
import { getObjectLanguage } from '../../../_core/components/languages-component';
import { useOpenModalForm } from '../../hooks/modal-forms/use-open-modal-form.hook';
import { useOpenConfirmModal } from '../../hooks/modal-forms/use-open-confirm-modal-form.hook';
import { Download, Search, Edit, RemoveIcon, SendIcon } from '../../../_core/constants/icons.constants';
import { boxHeaderStyles, chipDeleteIconStyles } from '../../../_core/styles';
import CoreCalendar from '../../../_core/components/_ui/core-date-picker/core-calendar/core-calendar.component';
import {
	ArchiveIcon,
	ChipCloseIcon,
	ConfirmationDeleteIcon,
	GroupPeopleFilledIcon,
	SelectIcon,
} from '../../../_core/components/icons';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { EngagementImportModal } from '../../components/modal-import-form/modal-import-form.component';
import { ProfileRoutes } from '../../../profile/constants';
import { useSchoolFilter } from '../../../_core/hooks/use-school-filter';
import { CoreCheckbox } from '../../../_core/components/_ui/core-checkbox';
import { CoreOverflowTypographyTooltip } from '../../../_core/components/_ui/core-overflow-tooltip';
import { RowSelectionState, Updater } from '@tanstack/react-table';
import { useOpenModalFormBulkEdit } from '../../hooks/modal-forms/use-open-modal-bulk-edit-form.hook';
import dayjs from 'dayjs';
import { EngagementsPieChart } from '../../components/charts/donut-chart.component';
import { EngagementColumnName } from '../../hooks/table-columns/engagements-table-columns.hook';
import { AllowedEngagementActions } from '../../components/engagements-table/engagements-table-actions.component';
import { useGetDistrictQuery } from '../../../../store/api/district.api';
import { useOpenRemindersConfirmModal } from '../../hooks/modal-forms/use-open-reminders-modal-form.hook';
import { CoreSelect, CoreSelectMenuItem } from '../../../_core/components/_ui/core-select';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { setFilters } from '../../store/engagementFilters.slice';
import isEqual from 'lodash/isEqual';
import { useCurrentAccessTokenSelector } from '../../../auth';

export type EngagementsListPageProps = {
	isAdminRoute: boolean;
	engagementColumnsList: EngagementColumnName[];
	allowedEngagementActions: AllowedEngagementActions[];
	providersList?: ProviderDto[];
	familiesList?: FamilyDto[];
	childrenList?: ChildDto[];
	currentDistrictId?: string | null;
	fieldsListFromDistrict?: CustomFieldDto[];
	permission?: PermissionDto;
	fieldsList?: CustomFieldDto[];
	statusesList?: EngagementStatusDto[];
	engagementLinguistLinkColumnsList?: EngagementColumnName[];
};

export const EngagementsListPage: FC<EngagementsListPageProps> = (props) => {
	const {
		currentDistrictId,
		isAdminRoute,
		permission,
		engagementColumnsList,
		allowedEngagementActions,
		providersList,
		familiesList,
		fieldsListFromDistrict,
		childrenList,
		fieldsList,
		statusesList,
		engagementLinguistLinkColumnsList,
	} = props;

	const { enqueueSnackbar } = useSnackbar();

	const [t] = useTranslation();
	const navigate = useNavigate();
	const isFirstRender = useRef(true);

	const currentToken = useCurrentAccessTokenSelector();
	const isSuperAdmin = currentToken?.payload.sur === UserRolesEnum.SuperAdmin;

	const dispatch = useAppDispatch();
	const savedFilters = useAppSelector((state) => state.engagementFilters);

	const { data: currentDistrict } = useGetDistrictQuery({ id: currentDistrictId }, { skip: !currentDistrictId });
	const isLinguistLink = !!currentDistrict?.districtLinguistLinkId;
	const isLL = isLinguistLink;

	const { data: schoolsList } = useGetSchoolsLinguistLinkQuery();

	const schoolsFamilyLink = fieldsList?.filter((field) => field.name === 'School');

	const schoolsAsField = {
		options: schoolsList ?? [],
	};

	const schoolField = isAdminRoute
		? [...(schoolsFamilyLink ?? []), schoolsAsField]
		: fieldsListFromDistrict?.find((field) => field.name === 'School');

	const schoolOptionsNonLL = Array.isArray(schoolField)
		? schoolField.flatMap((field) =>
				(field.options || []).map((opt) => ({
					_id: String(opt._id),
					text: opt.text ?? '',
				}))
		  )
		: (schoolField?.options || []).map((opt) => ({
				_id: String(opt._id),
				text: opt.text ?? '',
		  }));

	const schoolOptions = isLL ? schoolsList ?? [] : schoolOptionsNonLL;

	const lastFiltersRef = useRef(savedFilters);

	const [searchParams, setSearchParams] = useSearchParams();

	const [showArchived, setShowArchived] = useState(false);
	const handleToggleArchived = () => {
		setShowArchived((prev) => !prev);
		resetPage();
	};

	const [initialSearch] = useState(savedFilters.search || '');
	const { register, search } = useSearch(initialSearch);

	const [initialLanguages] = useState(savedFilters.languages || []);
	const {
		register: registerLang,
		getValues: getValuesLang,
		control: controlLang,
		languages,
	} = useLanguageFilter(initialLanguages);

	const [initialProviders] = useState(savedFilters.providers || []);
	const {
		register: registerProvider,
		getValues: getValuesProvider,
		control: controlProvider,

		providers,
		providerIds,
	} = useProviderFilter(initialProviders);

	const [initialSchools] = useState(savedFilters.schools || []);
	const {
		register: registerSchool,
		getValues: getValuesSchool,
		control: controlSchool,
		schools,
		schoolIds,
	} = useSchoolFilter(initialSchools);

	const [initialDuration] = useState(savedFilters.duration || [null, null]);

	const {
		name: nameDuration,
		control: controlDuration,
		transformDuration,
		currentDuration,
	} = useDurationFilter(...initialDuration);

	const [withReminder, setWithReminder] = useState(savedFilters.withReminder);
	const [linkedToFamily, setLinkedToFamily] = useState(savedFilters.linkedToFamily);

	const { sortingArray, sortingParams, handleSortingChange } = useSortingParams();

	const { requestObject, meta, page, handleResetPage, itemsList, fetchMore } = usePaginatedData<EngagementDto>(
		isAdminRoute ? useGetEngagementsListUnifiedQuery : useGetEngagementsListQuery,
		{
			search,
			...sortingParams,
			languages: languages.join(','),
			providers: providerIds.join(','),
			district: currentDistrictId,
			archived: showArchived,
			start: transformDuration.start
				? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
			end: transformDuration.start ? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '',
			schools: schools.join(','),
		},
		{ refetchOnMountOrArgChange: true, keepPreviousData: true, skip: isLL }
	);

	const {
		requestObject: request,
		meta: metalLL,
		page: pageLL,
		handleResetPage: handleResetPageLl,
		itemsList: itemsListLL,
		fetchMore: fetchMoreLL,
	} = usePaginatedData<EngagementDto>(
		useGetEngagementsFromLLByDistrictNameQuery,
		{
			search,
			...sortingParams,
			languages: languages.join(','),
			schools: schools.join(','),
			//providers: providerIds.join(','),
			start: transformDuration.start
				? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
			end: transformDuration.start ? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '',
			district: currentDistrictId,
			withReminder,
			linkedToFamily,
			archived: showArchived,
		},
		{ refetchOnMountOrArgChange: true, skip: !isLL }
	);

	const handleOnView = (e: React.MouseEvent<HTMLElement>, engagement: EngagementDto) => {
		if (!engagement) return;

		const baseParams = { engagementId: engagement._id };

		let targetPath: string;

		if (isAdminRoute) {
			if (engagement.isFromSecondary) {
				targetPath = generatePath(ProfileRoutes.EngagementDetailsLL, {
					...baseParams,
					engagementItemId: engagement.projectItemId,
				});
			} else {
				targetPath = generatePath(ProfileRoutes.EngagementDetails, baseParams);
			}
		} else if (isLL) {
			targetPath = generatePath(DistrictRoutes.Engagements.DetailsLL, {
				...baseParams,
				engagementItemId: engagement.projectItemId,
			});
		} else {
			targetPath = generatePath(DistrictRoutes.Engagements.Details, baseParams);
		}

		if (isLL || engagement.isFromSecondary) {
			const url = new URL(window.location.origin + targetPath);
			url.searchParams.set('isLinguistLink', 'true');
			targetPath = url.pathname + url.search;
		}
		
		if (isSuperAdmin && !isAdminRoute) {
			const url = new URL(window.location.origin + targetPath);
			url.searchParams.set('districtId', engagement.districtId);
			targetPath = url.pathname + url.search;
		}

		if (e.metaKey || e.ctrlKey || e.button === 1) {
			window.open(targetPath, '_blank', 'noopener,noreferrer');
			return;
		}

		e.preventDefault();
		navigate(targetPath, {
			state: { isLinguistLink: isLL || engagement.isFromSecondary },
		});
	};

	const currentPage = isLL ? pageLL : page;
	const resetPage = isLL ? handleResetPageLl : handleResetPage;
	const currentRequestObject = isLL ? request : requestObject;
	const { refetch, isFetching, isLoading, isUninitialized } = currentRequestObject;

	const handlePagination = () => {
		resetPage();

		if (currentPage === 1) {
			refetch?.();
		}
	};

	useEffect(() => {
		const newFilters = {
			search,
			languages,
			providers,
			schools,
			duration: currentDuration,
			withReminder,
			linkedToFamily,
			//mode,
			sortingArray,
		};

		if (isFirstRender.current) {
			lastFiltersRef.current = newFilters as any;
			return;
		}

		if (!isEqual(lastFiltersRef.current, newFilters)) {
			dispatch(setFilters(newFilters));

			lastFiltersRef.current = newFilters as any;
		}
	}, [
		search,
		languages,
		providers,
		schools,
		currentDuration,
		withReminder,
		linkedToFamily,
		//	mode,
		sortingArray,
		dispatch,
	]);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		if (currentPage !== 1) resetPage();
	}, [sortingArray, search, languages, providers, schools, transformDuration, withReminder, linkedToFamily, resetPage]);

	const [generateEngagementsCustomFieldFileUrl] = useGenerateEngagementsCustomFieldFileUrlMutation();

	const [createEngagement] = useCreateEngagementMutation();
	const fetchActionCreate = async (data: any) => {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		await createEngagement({ ...data, timezone }).unwrap();
	};
	const handleOpenModalCreate = useOpenModalForm({
		featchAction: fetchActionCreate,
		handlePagination,
		isCreate: true,
		districtId: currentDistrictId,
		submitButtonName: t('engagements.forms.buttons.create'),
		toastText: 'Engagement record was successfully created',
		fieldsListFromDistrict,
		providersList,
		familiesList,
		childrenList,
		statusesList,
		generateFileUrlFunction: generateEngagementsCustomFieldFileUrl,
	});

	const [updateEngagement] = useUpdateEngagementMutation();
	const featchActionEdit = async (data: any) => {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		await updateEngagement({
			id: data?._id?.toString(),
			data: {
				...data,
				timezone,
			},
		}).unwrap();
	};
	const handleOpenModalEdit = useOpenModalForm({
		featchAction: featchActionEdit,
		handlePagination,
		isCreate: false,
		districtId: currentDistrictId,
		submitButtonName: t('engagements.forms.buttons.save'),
		toastText: 'Engagement record was successfully updated',
		fieldsListFromDistrict,
		providersList,
		familiesList,
		childrenList,
		statusesList,
		generateFileUrlFunction: generateEngagementsCustomFieldFileUrl,
	});

	const [deleteEngagement] = useDeleteEngagementMutation();
	const featchActionDelete = async (data: EngagementDto) => {
		await deleteEngagement({ id: data._id }).unwrap();
	};
	const handleOpenDeleteConfirmModal = useOpenConfirmModal({
		featchAction: featchActionDelete,
		handlePagination,
		title: t('engagements.forms.delete-title'),
		description: t('engagements.forms.delete-description'),
		icon: <ConfirmationDeleteIcon sx={{ fontSize: '40px', color: (theme) => theme.colors.red }} />,
		submitButtonName: t('engagements.forms.buttons.delete'),
		toastText: 'Engagement record was successfully deleted',
		danger: true,
	});

	const [selectionMode, setSelectionMode] = useState(false);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [selectedEngagements, setSelectedEngagements] = useState<Record<string, EngagementDto>>({});

	const { data: allFilteredEngagements, isLoading: isLoadingAll } = useGetEngagementsListQuery(
		{
			page: 1,
			manualTake: 1000,
			take: 1000,
			skip: 0,
			search,
			...sortingParams,
			languages: languages.join(','),
			providers: providerIds.join(','),
			district: currentDistrictId,
			archived: showArchived,
			start: transformDuration.start
				? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
			end: transformDuration.start ? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '',
			schools: schools.join(','),
			takeAll: true,
		},
		{ refetchOnMountOrArgChange: true, skip: !selectionMode || isLinguistLink }
	);

	const { data: linguistLinkFilteredEngagements, isLoading: isLoadingAllLinguistLink } =
		useGetSelectedEngagementsFromLLQuery(
			{
				search,
				...sortingParams,
				languages: languages.join(','),

				district: currentDistrictId,

				start: transformDuration.start
					? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',
				end: transformDuration.start
					? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',
				withReminder,
				linkedToFamily,
			},
			{ refetchOnMountOrArgChange: true, skip: !selectionMode || !isLinguistLink }
		);

	const onRowSelectionChange = (updater: Updater<RowSelectionState>) => {
		const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;

		setRowSelection(newSelection);
		setSelectedEngagements((prev) => {
			const updated: Record<string, EngagementDto> = {};

			const sourceEngagements = isLinguistLink ? linguistLinkFilteredEngagements : allFilteredEngagements?.data;

			Object.entries(newSelection).forEach(([id, isSelected]) => {
				if (isSelected) {
					const engagement = sourceEngagements?.find((e: EngagementDto) => e._id === id) || prev[id];
					if (engagement) {
						updated[id] = engagement;
					}
				}
			});

			return updated;
		});
	};

	const [updateBulkEngagements] = useUpdateBulkEngagementsMutation();

	const featchActionBulkEdit = async (data: any) => {
		try {
			const selectedIds = data.ids;

			const updateData = { ...data.updateData };

			await updateBulkEngagements({ ids: selectedIds, updateData }).unwrap();

			setRowSelection((prev) => {
				const newSelection = { ...prev };

				selectedIds.forEach((_id: string) => {
					delete newSelection[_id];
				});
				return newSelection;
			});
			setSelectedEngagements((prev) => {
				const updated = { ...prev };
				selectedIds.forEach((_id: string) => {
					delete updated[_id];
				});
				return updated;
			});
		} catch (error) {
			console.error('Error updating bulk engagements:', error);
		}
	};

	const handleOpenBulkModalEdit = useOpenModalFormBulkEdit({
		featchAction: featchActionBulkEdit,
		handlePagination,
		isCreate: false,
		districtId: currentDistrictId,
		submitButtonName: t('engagements.forms.buttons.save'),
		toastText: 'Engagement records were successfully updated',
		fieldsListFromDistrict,
		providersList,
		familiesList,
		statusesList,
		childrenList,
	});

	const [sendRemindersLinguistLinkEngagements] = useSendRemindersLinguistLinkEngagementsMutation();
	const fetchActionBulkSendReminders = async (selectedEngagements: EngagementDto[]) => {
		try {
			const selectedIds = selectedEngagements.map((e) => e._id);
			const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

			const response = await sendRemindersLinguistLinkEngagements({
				engagementIds: selectedIds,
				timeZone,
			}).unwrap();

			setRowSelection((prev) => {
				const newSelection = { ...prev };
				selectedEngagements.forEach((engagement) => {
					delete newSelection[engagement._id];
				});
				return newSelection;
			});
			setSelectedEngagements((prev) => {
				const updated = { ...prev };
				selectedIds.forEach((_id) => {
					delete updated[_id];
				});
				return updated;
			});
			return response;
		} catch (error) {
			console.error('Bulk send reminders failed:', error);
			return error ?? null;
		}
	};
	const fetchActionSendReminder = async (engagement: EngagementDto) => {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const response = await sendRemindersLinguistLinkEngagements({
			engagementIds: [engagement._id],
			timeZone,
		}).unwrap();

		return response;
	};

	const handleSendReminder = useOpenRemindersConfirmModal({
		featchAction: fetchActionSendReminder,
		handlePagination,
		title: t('engagements.forms.send-reminder'),
		description: t('engagements.forms.send-reminder-description-one'),
		//icon: <ConfirmationDeleteIcon sx={{ fontSize: '40px', color: (theme) => theme.colors.red }} />,
		submitButtonName: t('engagements.forms.buttons.confirm'),
		toastText: 'Engagement reminder were successfully sent',
		danger: false,
	});

	const handleOpenBulkSendReminder = useOpenRemindersConfirmModal({
		featchAction: fetchActionBulkSendReminders,
		handlePagination,
		title: t('engagements.forms.send-reminder'),
		description: t('engagements.forms.send-reminder-description'),
		//icon: <ConfirmationDeleteIcon sx={{ fontSize: '40px', color: (theme) => theme.colors.red }} />,
		submitButtonName: t('engagements.forms.buttons.send'),
		toastText: 'Engagement reminders were successfully sent',
		danger: false,
	});

	const [deleteEngagements] = useBulkDeleteEngagementsMutation();

	const fetchActionBulkDelete = async (selectedEngagements: EngagementDto[]) => {
		try {
			const selectedIds = selectedEngagements.map((e) => e._id);
			await deleteEngagements({ ids: selectedEngagements.map((e) => e._id) }).unwrap();

			setRowSelection((prev) => {
				const newSelection = { ...prev };
				selectedEngagements.forEach((engagement) => {
					delete newSelection[engagement._id];
				});
				return newSelection;
			});
			setSelectedEngagements((prev) => {
				const updated = { ...prev };
				selectedIds.forEach((_id) => {
					delete updated[_id];
				});
				return updated;
			});
		} catch (error) {
			console.error('Bulk delete failed:', error);
		}
	};

	const handleOpenBulkDeleteConfirmModal = useOpenConfirmModal({
		featchAction: fetchActionBulkDelete,
		handlePagination,
		title: t('engagements.forms.delete-title'),
		description: t('engagements.forms.delete-description'),
		icon: <ConfirmationDeleteIcon sx={{ fontSize: '40px', color: (theme) => theme.colors.red }} />,
		submitButtonName: t('engagements.forms.buttons.delete'),
		toastText: 'Engagement records were successfully deleted',
		danger: true,
	});

	const [archiveEngagement] = useArchiveEngagementMutation();

	const fetchActionArchive = async (data: EngagementDto) => {
		await archiveEngagement({ id: data._id }).unwrap();
	};

	const [unarchiveEngagement] = useUnarchiveEngagementMutation();

	const fetchActionUnarchive = async (data: EngagementDto) => {
		await unarchiveEngagement({ id: data._id }).unwrap();
	};

	const handleArchiveConfirmModal = useOpenConfirmModal({
		featchAction: fetchActionArchive,
		handlePagination,
		title: t('engagements.forms.archive-title'),
		description: t('engagements.forms.archive-description'),
		icon: <ArchiveIcon sx={{ fontSize: '18px', color: (theme) => theme.colors.red }} />,
		danger: true,
		submitButtonName: t('engagements.forms.buttons.archive'),
		toastText: 'Engagement record was successfully archived.',
	});

	const [archiveEngagements] = useBulkArchiveEngagementsMutation();

	const featchActionArchiveBulk = async (selectedEngagements: EngagementDto[]) => {
		try {
			const selectedIds = selectedEngagements.map((e) => e._id);
			await archiveEngagements({ ids: selectedEngagements.map((e) => e._id) }).unwrap();

			setRowSelection((prev) => {
				const newSelection = { ...prev };
				selectedEngagements.forEach((engagement) => {
					delete newSelection[engagement._id];
				});
				return newSelection;
			});
			setSelectedEngagements((prev) => {
				const updated = { ...prev };
				selectedIds.forEach((_id) => {
					delete updated[_id];
				});
				return updated;
			});
		} catch (error) {
			console.error('Bulk archive failed:', error);
		}
	};

	const handleOpenBulkArchiveConfirmModal = useOpenConfirmModal({
		featchAction: featchActionArchiveBulk,
		handlePagination,
		title: t('engagements.forms.archive-title'),
		description: t('engagements.forms.archive-description'),
		icon: <ArchiveIcon sx={{ fontSize: '18px', color: (theme) => theme.colors.red }} />,
		danger: true,
		submitButtonName: t('engagements.forms.buttons.archive'),
		toastText: 'Engagement records were successfully archived',
	});

	const handleUnarchiveConfirmModal = useOpenConfirmModal({
		featchAction: fetchActionUnarchive,
		handlePagination,
		title: t('engagements.forms.unarchive-title'),
		description: t('engagements.forms.unarchive-description'),
		icon: <ArchiveIcon sx={{ fontSize: '18px', color: (theme) => theme.colors.red }} />,
		danger: true,
		submitButtonName: t('engagements.forms.buttons.unarchive'),
		toastText: 'Engagement record was successfully unarchived.',
	});

	const handleOnSendEmail = () => {
		console.log('onSendEmail');
	};
	const handleOnSendSMS = () => {
		console.log('onSendSMS');
	};

	const [exportEngagementsToCSV] = useExportEngagementsToCSVMutation();
	const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	const handleExport = async () => {
		try {
			const response = await exportEngagementsToCSV({
				search,
				languages: languages.join(','),
				providers: providerIds.join(','),
				district: currentDistrictId,
				archived: showArchived,
				start: transformDuration.start
					? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',
				end: transformDuration.start
					? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',
				schools: schools.join(','),
				timeZone: userTimeZone,
			}).unwrap();
			const fileUrl = response.fileUrl;

			enqueueSnackbar(t('engagements.export'), {
				variant: 'info',
				autoHideDuration: 3000,
			});

			const link = document.createElement('a');
			link.href = fileUrl;

			document.body.appendChild(link);

			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error('Failed to export CSV:', error);
		}
	};

	const handleToggleSelection = () => {
		setSelectionMode((prev) => {
			const next = !prev;
			if (!next) {
				setRowSelection({});
				setSelectedEngagements({});
			}
			return next;
		});
	};

	const { openModal, closeModal } = useMuiModal(EngagementImportModal);

	const handleOpenImportModal = () => {
		openModal({
			submitAction: async (formData: FormData) => Promise.resolve(),
			closeModal,
			districtId: currentDistrictId,
			providersList,
			familiesList,
			childrenList,
			fieldsListFromDistrict,
			statusesList,
			generateEngagementsCustomFieldFileUrl,
		});
	};

	const { data: pieChartData } = useGetEngagementPieChartDataQuery(
		{
			start:
				currentDuration?.length && currentDuration[0]
					? dayjs(currentDuration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',

			end:
				currentDuration?.length && currentDuration[1]
					? dayjs(currentDuration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',
			schools: schools?.join(','),
			district: currentDistrictId,
			languages: languages?.join(','),
			providers: providerIds?.join(','),
			archived: showArchived,
		},
		{ refetchOnMountOrArgChange: true }
	);

	const { data: activeEngagementsCount } = useGetActiveEngagementCountQuery(
		{
			start:
				currentDuration?.length && currentDuration[0]
					? dayjs(currentDuration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',

			end:
				currentDuration?.length && currentDuration[1]
					? dayjs(currentDuration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
					: '',
			schools: schools?.join(','),
			district: currentDistrictId,
			languages: languages?.join(','),
			providers: providerIds?.join(','),
			archived: showArchived,
		},
		{ refetchOnMountOrArgChange: true }
	);

	return (
		<Container
			maxWidth={false}
			disableGutters
			sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1, p: 0 }}
		>
			<Box sx={{ width: '100%', height: '160px', display: 'flex', gap: 1 }}>
				<Paper elevation={0} sx={{ flex: 1 }}>
					<Box
						sx={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							padding: 3,
						}}
					>
						<GroupPeopleFilledIcon sx={{ fontSize: '29px', color: (theme) => theme.colors.lightBlue }} />
						<Typography
							sx={{
								color: (theme) => theme.colors.lightBlue,
								fontSize: '56px',
								lineHeight: '100%',
								fontWeight: 500,
							}}
						>
							{activeEngagementsCount}
						</Typography>
						<Typography
							sx={{
								color: (theme) => theme.colors.grayLightBlue,
								fontSize: '14px',
								lineHeight: '100%',
								fontWeight: 500,
							}}
						>
							Active Engagements
						</Typography>
					</Box>
				</Paper>
				<Paper elevation={0} sx={{ flex: 1 }}>
					<Box sx={{ height: '100%', p: '12px' }}>
						<EngagementsPieChart data={pieChartData && pieChartData[0]?.languages} title="Language Distribution" />
					</Box>
				</Paper>
				<Paper elevation={0} sx={{ flex: 1 }}>
					<Box sx={{ height: '100%', p: '12px' }}>
						<EngagementsPieChart
							data={pieChartData && pieChartData[0]?.statuses}
							title="Engagement Statuses"
							useRandomColor={isLL || isAdminRoute ? true : false}
						/>
					</Box>
				</Paper>
				<Paper elevation={0} sx={{ flex: 1 }}>
					<Box sx={{ height: '100%', p: '12px' }}>
						<EngagementsPieChart
							data={pieChartData && pieChartData[0]?.programs}
							title="Program Distribution"
							useRandomColor={true}
						/>
					</Box>
				</Paper>
				<Paper elevation={0} sx={{ flex: 1 }}>
					<Box sx={{ height: '100%', p: '12px' }}>
						<EngagementsPieChart
							data={pieChartData && pieChartData[0]?.surveyStatuses}
							title="Survey Statuses"
							useRandomColor={false}
							colorsEnum={SurveyStatusColorEnum}
						/>
					</Box>
				</Paper>
			</Box>
			<Box
				sx={{
					width: '100%',
					flex: '1 1 0%',
					overflow: 'hidden',
				}}
			>
				<Paper
					elevation={0}
					sx={{
						width: '100%',
						height: '100%',
						p: 3,
						display: 'flex',
						flexDirection: 'column',
						position: 'relative',
						transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',
						borderRadius: '12px 12px 0px 0px',
					}}
				>
					<Box sx={boxHeaderStyles}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								flexWrap: 'wrap',
								gap: 1,
							}}
						>
							<CoreTextField
								{...register('search')}
								placeholder={t('common.search')}
								hasEndAdornment
								endAdornment={
									<InputAdornment position="end">
										<Search />
									</InputAdornment>
								}
								sx={{ minWidth: '140px', maxWidth: '190px' }}
							/>
							<CoreAutoComplete
								multiple={true}
								{...registerLang('languages')}
								control={controlLang}
								controlSx={{
									minWidth: '180px',
									maxWidth: '240px',
								}}
								isFilter
								options={Object.values(LanguageEnum)}
								getOptionLabel={(option) => {
									const language = getObjectLanguage(option);

									return language ? language.name : '';
								}}
								selectOnFocus={true}
								isOptionEqualToValue={(option, value) => option === value}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValuesLang('languages')?.length ? t('common.filter-lang') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
											'& .MuiInputBase-input': {
												height: '100%',
												padding: '0',
											},
										}}
									/>
								)}
								renderOption={(props, option, { selected }) => {
									const language = getObjectLanguage(option);

									return (
										<MenuItem
											{...props}
											key={language?.value}
											sx={{
												display: 'flex',
												alignItems: 'center',
											}}
										>
											<CoreCheckbox checked={selected} sx={{ mr: 1, p: 0, ml: 0 }} />
											<CoreOverflowTypographyTooltip>{language?.name}</CoreOverflowTypographyTooltip>
										</MenuItem>
									);
								}}
								renderTags={(value, getTagProps) => {
									const count = value.length - 1;

									return [
										value[0] && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: 0 })}
												label={getObjectLanguage(value[0])?.name}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
										count > 0 && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: count })}
												label={`+${count}`}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
									];
								}}
							/>
							<CoreAutoComplete
								multiple={true}
								{...registerProvider('providers')}
								control={controlProvider}
								controlSx={{ minWidth: '170px', maxWidth: '240px' }}
								isFilter
								options={providersList ?? []}
								getOptionLabel={(option) => option.fullName || ''}
								selectOnFocus={true}
								isOptionEqualToValue={(option, value) => option._id === value._id}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValuesProvider('providers')?.length ? t('common.filter-provider') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
											'& .MuiInputBase-input': {
												height: '100%',
												padding: '0',
											},
											'& .MuiOutlinedInput-root': {
												'&.Mui-focused fieldset': {
													borderColor: (theme) => theme.colors.blue,
												},
											},
										}}
									/>
								)}
								renderOption={(props, option, { selected }) => (
									<MenuItem
										{...props}
										key={option._id}
										sx={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />

										<Avatar
											src={option.imageUrl}
											alt={option.fullName}
											style={{ width: 24, height: 24, marginRight: 18, borderRadius: '6px' }}
										/>
										<CoreOverflowTypographyTooltip>{option.fullName}</CoreOverflowTypographyTooltip>
									</MenuItem>
								)}
								renderTags={(value, getTagProps) => {
									const count = value.length - 1;

									return [
										value[0] && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: 0 })}
												label={value[0]?.fullName}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
										count > 0 && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: count })}
												label={`+${count}`}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
									];
								}}
							/>
							<CoreAutoComplete
								multiple={true}
								{...registerSchool('schools')}
								control={controlSchool}
								controlSx={{
									minWidth: '160px',
									maxWidth: '240px',
								}}
								isFilter
								options={schoolOptions.map((s) => s._id)}
								getOptionLabel={(option) => {
									const school = schoolOptions.find((s) => s._id === option);

									return school ? school.text : '';
								}}
								selectOnFocus={true}
								isOptionEqualToValue={(option, value) => option === value}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValuesSchool('schools')?.length ? t('common.filter-school') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
										}}
									/>
								)}
								renderOption={(props, option, { selected }) => {
									const school = schoolOptions.find((s) => s._id === option);

									return (
										<MenuItem {...props} key={school?._id?.toString()}>
											<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />
											<CoreOverflowTypographyTooltip>{school?.text}</CoreOverflowTypographyTooltip>
										</MenuItem>
									);
								}}
								renderTags={(value, getTagProps) => {
									const count = value.length - 1;

									return [
										value[0] && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: 0 })}
												label={schoolOptions.find((s) => s._id === value[0])?.text}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
										count > 0 && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: count })}
												label={`+${count}`}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
									];
								}}
							/>
							<Box sx={{ minWidth: '200px', maxWidth: '270px', maxHeight: '48px' }}>
								<Controller
									name={nameDuration}
									control={controlDuration}
									render={({ field: { value, ...fieldWithoutValue } }) => (
										<CoreCalendar
											{...fieldWithoutValue}
											selectsRange
											inline={false}
											startDate={currentDuration?.length ? currentDuration[0] : null}
											endDate={currentDuration?.length ? currentDuration[1] : null}
											onChange={(dates: [Date | null, Date | null]) => {
												fieldWithoutValue.onChange(dates);
											}}
											onKeyDown={(e) => {
												e.preventDefault();
											}}
											onClear={() => {
												fieldWithoutValue.onChange([null, null]);
											}}
										/>
									)}
								/>
							</Box>
							{isLinguistLink && (
								<>
									<Box sx={{ minWidth: '200px', maxWidth: '270px', maxHeight: '48px' }}>
										<CoreSelect
											value={withReminder === undefined ? '' : withReminder ? 'true' : 'false'}
											placeholder={t('engagements.forms.placeholders.withReminder') ?? ''}
											fullWidth
											sx={{ minWidth: '140px', maxWidth: '190px' }}
											onChange={(e) => {
												const val = e.target.value;
												setWithReminder(val === '' ? undefined : val === 'true');
											}}
										>
											<CoreSelectMenuItem value="">All</CoreSelectMenuItem>
											<CoreSelectMenuItem value="true">{t('engagements.forms.with-reminder')}</CoreSelectMenuItem>
											<CoreSelectMenuItem value="false">{t('engagements.forms.no-reminder')}</CoreSelectMenuItem>
										</CoreSelect>
									</Box>
									<Box sx={{ minWidth: '200px', maxWidth: '270px', maxHeight: '48px' }}>
										<CoreSelect
											value={linkedToFamily === undefined ? '' : linkedToFamily ? 'true' : 'false'}
											placeholder={t('engagements.linked-to-family') ?? ''}
											fullWidth
											sx={{ minWidth: '140px', maxWidth: '190px' }}
											onChange={(e) => {
												const val = e.target.value;
												setLinkedToFamily(val === '' ? undefined : val === 'true');
											}}
										>
											<CoreSelectMenuItem value="">All</CoreSelectMenuItem>
											<CoreSelectMenuItem value="true">{t('engagements.connected')}</CoreSelectMenuItem>
											<CoreSelectMenuItem value="false">{t('engagements.not-connected')}</CoreSelectMenuItem>
										</CoreSelect>
									</Box>
								</>
							)}
						</Box>
						<Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
							<CoreButton
								tooltipTitle={t('buttons.tooltips.export')}
								variant="base"
								circle
								size="large"
								disabled={isLinguistLink}
								onClick={handleExport}
								sx={{
									marginLeft: 'auto',
									display: 'flex',
									gap: 1,
									color: isLinguistLink ? '#9EC6DE' : 'primary.main',
									'&:disabled:not(.core-button-loading)': {
										borderColor: ' #9EC6DE',
										color: '#9EC6DE',
									},
								}}
							>
								<Download style={{ color: isLinguistLink ? '#9EC6DE' : '#3C8DBC' }} />
							</CoreButton>

							{permission?.create ? (
								<CoreButton
									sx={{
										marginLeft: 'auto',
										display: 'flex',
										gap: 1,
										'&:disabled:not(.core-button-loading)': {
											borderColor: ' #9EC6DE',
										},
									}}
									variant="base"
									circle
									tooltipTitle={t('buttons.tooltips.import')}
									size="large"
									disabled={showArchived || isLinguistLink}
									onClick={handleOpenImportModal}
								>
									<FileUploadOutlinedIcon
										sx={{
											color: showArchived || isLinguistLink ? '#9EC6DE' : 'primary.main',
										}}
									/>
								</CoreButton>
							) : null}

							{!isAdminRoute ? (
								<CoreButton
									tooltipTitle={t('buttons.tooltips.bulk-actions')}
									variant="base"
									circle
									size="large"
									disabled={showArchived}
									onClick={handleToggleSelection}
									sx={[
										{
											'&:disabled:not(.core-button-loading)': {
												borderColor: ' #9EC6DE',
												color: ' #9EC6DE',
											},
										},
										selectionMode
											? {
													bgcolor: (theme) => theme.colors.lightBlue,
													'&:hover': {
														bgcolor: (theme) => theme.colors.lightBlue,
													},
											  }
											: {},
									]}
								>
									<SelectIcon
										sx={{
											fontSize: '16px',
											color: (theme: any) =>
												showArchived ? '#9EC6DE' : selectionMode ? theme.colors.white : theme.colors.lightBlue,
										}}
									/>
								</CoreButton>
							) : null}

							{permission?.create ? (
								<CoreButton
									circle
									tooltipTitle={t('buttons.tooltips.create')}
									size="large"
									onClick={() => {
										handleOpenModalCreate();
									}}
									disabled={showArchived || isLinguistLink}
									sx={(theme) => ({
										marginLeft: 'auto',
										bgcolor: showArchived ? '#FCDDD5' : theme.colors.orangeDeep,
										color: 'white',
										border: 'none',
										'&.Mui-disabled': {
											color: 'white',
											bgcolor: '#FCDDD5',
											border: 'none',
										},
										'& svg': {
											color: 'white',
										},
									})}
								>
									<Add />
								</CoreButton>
							) : null}
							<Divider orientation="vertical" flexItem />

							<CoreButton
								variant="base"
								circle
								tooltipTitle={showArchived ? t('buttons.tooltips.unarhived-view') : t('buttons.tooltips.arhived-view')}
								size="large"
								disabled={selectionMode}
								onClick={handleToggleArchived}
								sx={[
									{
										'&:disabled:not(.core-button-loading)': {
											borderColor: ' #9EC6DE',
										},
									},
									showArchived
										? {
												bgcolor: (theme) => theme.colors.lightBlue,
												'&:hover': {
													bgcolor: (theme) => theme.colors.lightBlue,
												},
										  }
										: {},
								]}
							>
								<ArchiveIcon
									sx={{
										fontSize: '18px',
										color: (theme) =>
											selectionMode ? '#9EC6DE' : showArchived ? theme.colors.white : theme.colors.lightBlue,
									}}
								/>
							</CoreButton>
						</Box>
					</Box>
					{isLinguistLink ? (
						<EngagementsTable
							engagementColumnsList={engagementLinguistLinkColumnsList ?? engagementColumnsList}
							data={itemsListLL ?? []}
							loading={isFetching || isLoading}
							isSkeletonLoader={pageLL === 1 && itemsListLL?.length && isFetching}
							isLinguistLink
							isAdminRoute={isAdminRoute}
							hasMore={metalLL?.hasNextPage}
							fetchMore={fetchMoreLL}
							allowedEngagementActions={allowedEngagementActions}
							sorting={sortingArray}
							onSortingChange={handleSortingChange}
							onView={(e: MouseEvent<HTMLElement, globalThis.MouseEvent>, engagement: EngagementDto) =>
								handleOnView(e, engagement)
							}
							onSendReminder={handleSendReminder}
							isSelectionMode={selectionMode}
							rowSelection={rowSelection}
							onRowSelectionChange={onRowSelectionChange}
							meta={{
								allFilteredEngagements: linguistLinkFilteredEngagements ?? [],
							}}
						/>
					) : (
						<EngagementsTable
							engagementColumnsList={engagementColumnsList}
							allowedEngagementActions={allowedEngagementActions}
							data={itemsList ?? []}
							loading={isFetching || isLoading}
							isSkeletonLoader={page === 1 && itemsList?.length && isFetching}
							isLinguistLink={isLinguistLink}
							isAdminRoute={isAdminRoute}
							hasMore={meta?.hasNextPage}
							onDelete={handleOpenDeleteConfirmModal}
							onEdit={handleOpenModalEdit}
							onView={(e: MouseEvent<HTMLElement, globalThis.MouseEvent>, engagement: EngagementDto) =>
								handleOnView(e, engagement)
							}
							onArchive={handleArchiveConfirmModal}
							onUnarchive={handleUnarchiveConfirmModal}
							onSendEmail={handleOnSendEmail}
							onSendSMS={handleOnSendSMS}
							sorting={sortingArray}
							onSortingChange={handleSortingChange}
							fetchMore={fetchMore}
							isSelectionMode={selectionMode}
							rowSelection={rowSelection}
							onRowSelectionChange={onRowSelectionChange}
							meta={{
								allFilteredEngagements: allFilteredEngagements?.data ?? [],
							}}
						/>
					)}
					{Object.keys(rowSelection).length > 0 && (
						<Box
							sx={{
								position: 'fixed',
								bottom: '16px',
								left: '50%',
								transform: 'translateX(-50%)',
								borderRadius: 2,
								border: '1px solid #9397A4',
								background: 'white',
								boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.20)',
								padding: 2,
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								zIndex: 1000,
								gap: 3,
							}}
						>
							<Typography>{Object.keys(rowSelection).length} engagement(s) selected</Typography>

							<Box sx={{ display: 'flex', gap: 1 }}>
								{isLinguistLink ? (
									<Tooltip title={t('buttons.tooltips.send')}>
										<CoreIconButton
											sx={{
												width: 32,
												height: 32,
											}}
											disabled={Object.keys(rowSelection).length === 0}
											onClick={() => handleOpenBulkSendReminder(Object.values(selectedEngagements))}
										>
											<SendIcon />
										</CoreIconButton>
									</Tooltip>
								) : (
									<>
										<Tooltip title={t('buttons.tooltips.edit')}>
											<CoreIconButton
												sx={{
													width: 32,
													height: 32,
												}}
												disabled={Object.keys(rowSelection).length === 0}
												onClick={() => handleOpenBulkModalEdit(Object.values(selectedEngagements))}
											>
												<Edit />
											</CoreIconButton>
										</Tooltip>
										<Tooltip title={t('buttons.tooltips.archive')}>
											<CoreIconButton
												sx={{
													width: 32,
													height: 32,
												}}
												onClick={() => handleOpenBulkArchiveConfirmModal(Object.values(selectedEngagements))}
											>
												<ArchiveIcon sx={{ fontSize: '16px', color: (theme) => theme.colors.lightBlue }} />
											</CoreIconButton>
										</Tooltip>
										<Tooltip title={t('buttons.tooltips.delete')}>
											<CoreIconButton
												sx={{
													width: 32,
													height: 32,
												}}
												disabled={Object.keys(rowSelection).length === 0}
												onClick={() => handleOpenBulkDeleteConfirmModal(Object.values(selectedEngagements))}
											>
												<RemoveIcon />
											</CoreIconButton>
										</Tooltip>
									</>
								)}
							</Box>
						</Box>
					)}
				</Paper>
			</Box>
		</Container>
	);
};
